import { resolveTeacherManualPalette } from "./teacherManualTheme";
import {
  TM,
  contentWidth,
  layoutCoverHeader,
  layoutSectionHeader,
  textBoxHeight,
} from "./teacherManualLayout";

const FONT = "Arial";

export const TEACHER_MANUAL_LAYOUT = {
  name: "TEACHER_MANUAL_16x9",
  width: TM.width,
  height: TM.height,
};

function paletteFor(slide) {
  return resolveTeacherManualPalette("light");
}

function padSlideNumber(index) {
  return `Slide ${String(index + 1).padStart(2, "0")}`;
}

function addAccentBars(slide, pptx, palette) {
  slide.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: TM.width,
    h: TM.accentH,
    fill: { color: palette.accent },
    line: { color: palette.accent, width: 0 },
  });
  slide.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: TM.height - TM.accentH,
    w: TM.width,
    h: TM.accentH,
    fill: { color: palette.accent, transparency: 88 },
    line: { color: palette.accent, transparency: 100, width: 0 },
  });
}

function addDecorations(slide, pptx, palette, kind) {
  slide.addShape(pptx.ShapeType.ellipse, {
    x: 12.05,
    y: -0.15,
    w: 1.55,
    h: 1.55,
    fill: { color: palette.deco, transparency: 40 },
    line: { color: palette.deco, transparency: 100, width: 0 },
  });
  if (kind === "overview") {
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 11.65,
      y: -0.28,
      w: 1.25,
      h: 1.25,
      rotate: 45,
      rectRadius: 0.1,
      fill: { color: "F3C77A", transparency: 30 },
      line: { color: "F3C77A", transparency: 100, width: 0 },
    });
    slide.addShape(pptx.ShapeType.roundRect, {
      x: 12.35,
      y: 0.28,
      w: 1.1,
      h: 1.1,
      rotate: 45,
      rectRadius: 0.1,
      fill: { color: "F3C77A", transparency: 45 },
      line: { color: "F3C77A", transparency: 100, width: 0 },
    });
  }
}

function addFooter(slide, pptx, palette, { index, footerTopic }) {
  slide.addShape(pptx.ShapeType.rect, {
    x: TM.marginX,
    y: TM.footerY - 0.08,
    w: contentWidth(),
    h: 0.01,
    fill: { color: palette.divider },
    line: { color: palette.divider, width: 0 },
  });
  slide.addText(padSlideNumber(index), {
    x: TM.marginX,
    y: TM.footerY,
    w: 2.2,
    h: TM.footerH,
    fontFace: FONT,
    fontSize: 10,
    bold: true,
    color: palette.accent,
    margin: 0,
    valign: "middle",
  });
  if (footerTopic) {
    slide.addText(footerTopic, {
      x: TM.width - TM.marginX - 4.4,
      y: TM.footerY,
      w: 4.4,
      h: TM.footerH,
      fontFace: FONT,
      fontSize: 10,
      color: palette.footerMuted,
      align: "right",
      margin: 0,
      valign: "middle",
      wrap: false,
    });
  }
}

function addKickerRow(slide, pptx, palette, kicker, box) {
  if (!kicker || !box) {
    return;
  }
  slide.addShape(pptx.ShapeType.rect, {
    x: TM.marginX,
    y: box.y + 0.09,
    w: 0.17,
    h: 0.03,
    fill: { color: palette.accent },
    line: { color: palette.accent, width: 0 },
  });
  slide.addText(String(kicker).toUpperCase(), {
    x: TM.marginX + 0.26,
    y: box.y,
    w: contentWidth() - 0.26,
    h: box.h,
    fontFace: FONT,
    fontSize: String(kicker).length > 52 ? 9 : 10,
    bold: true,
    color: palette.accent,
    margin: 0,
    valign: "middle",
    wrap: true,
  });
}

function cardLine(palette) {
  return { color: palette.cardBorder, width: 1 };
}

function createChromedSlide(pptx, slideModel, ctx, palette, kind) {
  const s = pptx.addSlide();
  s.addShape(pptx.ShapeType.rect, {
    x: 0,
    y: 0,
    w: "100%",
    h: "100%",
    fill: { color: palette.background },
    line: { color: palette.background, width: 0 },
  });
  addAccentBars(s, pptx, palette);
  addDecorations(s, pptx, palette, kind);
  addFooter(s, pptx, palette, {
    index: ctx.index,
    footerTopic: slideModel.footerTopic ?? slideModel.footerLabel,
  });
  return s;
}

function addSectionChrome(slide, pptx, palette, slideModel) {
  const header = layoutSectionHeader({
    title: slideModel.title,
    subtitle: slideModel.subtitle,
    kicker: slideModel.kicker,
  });
  addKickerRow(slide, pptx, palette, slideModel.kicker, header.kickerBox);
  slide.addText(slideModel.title ?? "", {
    x: TM.marginX,
    y: header.titleBox.y,
    w: contentWidth(),
    h: header.titleBox.h,
    fontFace: FONT,
    fontSize: header.titleBox.fontSize,
    bold: true,
    color: palette.text,
    margin: 0,
    valign: "top",
    wrap: true,
  });
  if (slideModel.subtitle && header.subtitleBox) {
    slide.addText(slideModel.subtitle, {
      x: TM.marginX,
      y: header.subtitleBox.y,
      w: contentWidth(),
      h: header.subtitleBox.h,
      fontFace: FONT,
      fontSize: header.subtitleBox.fontSize,
      color: palette.muted,
      margin: 0,
      valign: "top",
      wrap: true,
    });
  }
  slide.addShape(pptx.ShapeType.rect, {
    x: TM.marginX,
    y: header.dividerY,
    w: contentWidth(),
    h: 0.01,
    fill: { color: palette.divider },
    line: { color: palette.divider, width: 0 },
  });
  return header;
}

function addCoverSlide(pptx, slideModel, ctx) {
  const palette = paletteFor(slideModel);
  const s = createChromedSlide(pptx, slideModel, ctx, palette, "cover");
  const header = layoutCoverHeader({
    title: slideModel.title,
    subtitle: slideModel.subtitle,
    kicker: slideModel.kicker,
  });

  addKickerRow(s, pptx, palette, slideModel.kicker, header.kickerBox);

  s.addText(slideModel.title ?? "", {
    x: TM.marginX,
    y: header.titleBox.y,
    w: header.titleBox.w ?? contentWidth(),
    h: header.titleBox.h,
    fontFace: FONT,
    fontSize: header.titleBox.fontSize,
    bold: true,
    color: palette.text,
    margin: 0,
    valign: "top",
    wrap: true,
  });

  s.addShape(pptx.ShapeType.rect, {
    x: TM.marginX,
    y: header.dividerY,
    w: header.titleBox.w ?? contentWidth(),
    h: 0.01,
    fill: { color: palette.divider },
    line: { color: palette.divider, width: 0 },
  });

  if (slideModel.subtitle && header.subtitleBox) {
    s.addText(slideModel.subtitle, {
      x: TM.marginX,
      y: header.subtitleBox.y,
      w: header.subtitleBox.w ?? contentWidth(),
      h: header.subtitleBox.h,
      fontFace: FONT,
      fontSize: header.subtitleBox.fontSize,
      color: palette.muted,
      margin: 0,
      valign: "top",
      wrap: true,
    });
  }
}

function addDividerSlide(pptx, slideModel, ctx) {
  addCoverSlide(pptx, slideModel, ctx);
}

function addOverviewSlide(pptx, slideModel, ctx) {
  const palette = paletteFor(slideModel);
  const s = createChromedSlide(pptx, slideModel, ctx, palette, "overview");
  const header = addSectionChrome(s, pptx, palette, slideModel);
  const y = header.contentY;
  const h = header.contentH;
  const sidebarW = 3.45;
  const gap = 0.32;
  const focalW = contentWidth() - sidebarW - gap;
  const pad = 0.26;

  s.addShape(pptx.ShapeType.roundRect, {
    x: TM.marginX,
    y,
    w: focalW,
    h,
    rectRadius: TM.radius,
    fill: { color: palette.card },
    line: cardLine(palette),
  });
  s.addShape(pptx.ShapeType.roundRect, {
    x: TM.marginX + pad,
    y: y + pad + 0.04,
    w: 0.03,
    h: 0.2,
    rectRadius: 0.01,
    fill: { color: palette.accent },
    line: { color: palette.accent, width: 0 },
  });
  const focalTitleH = textBoxHeight(slideModel.focal?.title, focalW - pad * 2 - 0.14, 14, {
    maxLines: 2,
    bold: true,
    min: 0.28,
  });
  s.addText(slideModel.focal?.title ?? "", {
    x: TM.marginX + pad + 0.12,
    y: y + pad,
    w: focalW - pad * 2 - 0.12,
    h: focalTitleH,
    fontFace: FONT,
    fontSize: 14,
    bold: true,
    color: palette.text,
    margin: 0,
    wrap: true,
    valign: "top",
  });
  s.addText(slideModel.focal?.body ?? "", {
    x: TM.marginX + pad,
    y: y + pad + focalTitleH + 0.1,
    w: focalW - pad * 2,
    h: Math.max(0.8, h - pad * 2 - focalTitleH - 0.1),
    fontFace: FONT,
    fontSize: 12,
    color: palette.muted,
    margin: 0,
    valign: "top",
    wrap: true,
  });

  const sideX = TM.marginX + focalW + gap;
  const hasList = Boolean(slideModel.list?.items?.length);
  const highlightH = hasList ? Math.min(h * 0.48, 1.7) : h;

  if (slideModel.highlight) {
    s.addShape(pptx.ShapeType.roundRect, {
      x: sideX,
      y,
      w: sidebarW,
      h: highlightH,
      rectRadius: 0.1,
      fill: { color: palette.highlightFill },
      line: { color: palette.accentSoftBorder, width: 1 },
    });
    let sideY = y + 0.18;
    s.addText(slideModel.highlight.title ?? "", {
      x: sideX + 0.2,
      y: sideY,
      w: sidebarW - 0.4,
      h: 0.24,
      fontFace: FONT,
      fontSize: 12,
      bold: true,
      color: palette.accent,
      margin: 0,
    });
    sideY += 0.26;
    if (slideModel.highlight.accent) {
      s.addText(slideModel.highlight.accent, {
        x: sideX + 0.2,
        y: sideY,
        w: sidebarW - 0.4,
        h: 0.28,
        fontFace: FONT,
        fontSize: 14,
        bold: true,
        color: palette.text,
        margin: 0,
        wrap: true,
      });
      sideY += 0.3;
    }
    s.addText(slideModel.highlight.body ?? "", {
      x: sideX + 0.2,
      y: sideY,
      w: sidebarW - 0.4,
      h: Math.max(0.4, y + highlightH - sideY - 0.16),
      fontFace: FONT,
      fontSize: 11,
      color: palette.muted,
      margin: 0,
      valign: "top",
      wrap: true,
    });
  }

  if (hasList) {
    const listY = slideModel.highlight ? y + highlightH + 0.16 : y;
    const listH = slideModel.highlight ? h - highlightH - 0.16 : h;
    s.addShape(pptx.ShapeType.roundRect, {
      x: sideX,
      y: listY,
      w: sidebarW,
      h: listH,
      rectRadius: 0.1,
      fill: { color: palette.card },
      line: cardLine(palette),
    });
    s.addText(slideModel.list.title ?? "", {
      x: sideX + 0.2,
      y: listY + 0.16,
      w: sidebarW - 0.4,
      h: 0.24,
      fontFace: FONT,
      fontSize: 12,
      bold: true,
      color: palette.text,
      margin: 0,
    });
    const items = slideModel.list.items.slice(0, 4).map((item, index) => ({
      text: `${index + 1}. ${item.label ?? item.title ?? ""} ${item.text ?? ""}`.trim(),
      options: { breakLine: true, fontSize: 11, color: palette.muted, fontFace: FONT },
    }));
    s.addText(items, {
      x: sideX + 0.2,
      y: listY + 0.44,
      w: sidebarW - 0.4,
      h: Math.max(0.5, listH - 0.6),
      margin: 0,
      valign: "top",
      paraSpacing: 8,
    });
  }
}

function addCompareSlide(pptx, slideModel, ctx) {
  const palette = paletteFor(slideModel);
  const s = createChromedSlide(pptx, slideModel, ctx, palette, "compare");
  const header = addSectionChrome(s, pptx, palette, slideModel);
  const y = header.contentY;
  const h = header.contentH;
  const bridgeW = slideModel.bridge ? 1.15 : 0.18;
  const gap = 0.16;
  const cardW = (contentWidth() - bridgeW - gap * 2) / 2;

  addCompareCard(s, pptx, palette, {
    x: TM.marginX,
    y,
    w: cardW,
    h,
    card: slideModel.left,
    badgeFill: palette.accentSoft,
    badgeColor: palette.accent,
  });

  if (slideModel.bridge) {
    s.addText(String(slideModel.bridge).toUpperCase(), {
      x: TM.marginX + cardW + gap,
      y: y + h / 2 - 0.16,
      w: bridgeW,
      h: 0.32,
      fontFace: FONT,
      fontSize: 10,
      bold: true,
      color: "FFFFFF",
      align: "center",
      valign: "middle",
      fill: { color: palette.accent },
      shape: pptx.ShapeType.roundRect,
    });
  }

  addCompareCard(s, pptx, palette, {
    x: TM.marginX + cardW + gap + bridgeW + gap,
    y,
    w: cardW,
    h,
    card: slideModel.right,
    badgeFill: palette.pillMutedFill,
    badgeColor: palette.pillMutedText,
  });
}

function addCompareCard(slide, pptx, palette, { x, y, w, h, card, badgeFill, badgeColor }) {
  if (!card) {
    return;
  }
  const pad = 0.24;
  slide.addShape(pptx.ShapeType.roundRect, {
    x,
    y,
    w,
    h,
    rectRadius: TM.radius,
    fill: { color: palette.card },
    line: cardLine(palette),
  });

  let cursor = y + pad;
  if (card.badge) {
    slide.addText(String(card.badge).toUpperCase(), {
      x: x + pad,
      y: cursor,
      w: Math.min(2.1, w - pad * 2),
      h: 0.22,
      fontFace: FONT,
      fontSize: 9,
      bold: true,
      color: badgeColor,
      align: "center",
      valign: "middle",
      fill: { color: badgeFill },
      shape: pptx.ShapeType.roundRect,
    });
    cursor += 0.3;
  }

  const titleH = textBoxHeight(card.title, w - pad * 2, 15, {
    maxLines: 2,
    bold: true,
    min: 0.28,
  });
  slide.addText(card.title ?? "", {
    x: x + pad,
    y: cursor,
    w: w - pad * 2,
    h: titleH,
    fontFace: FONT,
    fontSize: 15,
    bold: true,
    color: palette.text,
    margin: 0,
    wrap: true,
    valign: "top",
  });
  cursor += titleH + 0.08;

  const bullets = (card.bullets ?? []).filter(Boolean).slice(0, 3);
  const bulletsH = bullets.length ? Math.min(0.85, bullets.length * 0.26) : 0;
  const available = y + h - pad - cursor - (bulletsH ? bulletsH + 0.1 : 0);
  const estimatedBody = textBoxHeight(card.body, w - pad * 2, 12, {
    maxLines: 8,
    min: 0.4,
  });
  const bodyH = Math.max(0.4, Math.min(available, estimatedBody));

  slide.addText(card.body ?? "", {
    x: x + pad,
    y: cursor,
    w: w - pad * 2,
    h: bodyH,
    fontFace: FONT,
    fontSize: 12,
    color: palette.muted,
    margin: 0,
    wrap: true,
    valign: "top",
  });
  cursor += bodyH + 0.08;

  if (bullets.length > 0) {
    slide.addText(
      bullets.map((bullet) => ({
        text: bullet,
        options: {
          bullet: { color: palette.accent, characterCode: "25CF" },
          breakLine: true,
          fontSize: 11,
          color: palette.muted,
          fontFace: FONT,
        },
      })),
      {
        x: x + pad,
        y: cursor,
        w: w - pad * 2,
        h: bulletsH,
        margin: 0,
        valign: "top",
      },
    );
  }
}

function addStepsSlide(pptx, slideModel, ctx) {
  const palette = paletteFor(slideModel);
  const s = createChromedSlide(pptx, slideModel, ctx, palette, "steps");
  const header = addSectionChrome(s, pptx, palette, slideModel);
  const y = header.contentY;
  const h = header.contentH;
  const steps = (slideModel.steps ?? []).slice(0, 3);
  const count = Math.max(steps.length, 1);
  const arrowW = count > 1 ? 0.28 : 0;
  const gap = 0.18;
  const cardW = (contentWidth() - (arrowW + gap) * (count - 1)) / count;
  const pad = 0.22;

  steps.forEach((step, index) => {
    const x = TM.marginX + index * (cardW + gap + arrowW);
    s.addShape(pptx.ShapeType.roundRect, {
      x,
      y,
      w: cardW,
      h,
      rectRadius: TM.radius,
      fill: { color: palette.card },
      line: cardLine(palette),
    });

    s.addShape(pptx.ShapeType.ellipse, {
      x: x + pad,
      y: y + pad,
      w: 0.38,
      h: 0.38,
      fill: { color: palette.accentSoft },
      line: { color: palette.accentSoft, width: 0 },
    });
    s.addText(String(index + 1).padStart(2, "0"), {
      x: x + pad,
      y: y + pad,
      w: 0.38,
      h: 0.38,
      fontFace: FONT,
      fontSize: 12,
      bold: true,
      color: palette.accent,
      align: "center",
      valign: "middle",
      margin: 0,
    });

    let cursor = y + pad + 0.5;
    const titleH = textBoxHeight(step.title, cardW - pad * 2, 13, {
      maxLines: 2,
      bold: true,
      min: 0.28,
    });
    s.addText(step.title ?? "", {
      x: x + pad,
      y: cursor,
      w: cardW - pad * 2,
      h: titleH,
      fontFace: FONT,
      fontSize: 13,
      bold: true,
      color: palette.text,
      margin: 0,
      wrap: true,
      valign: "top",
    });
    cursor += titleH + 0.08;

    const hasMeta = Boolean(step.metaLabel || step.metaValue);
    const metaH = hasMeta ? 0.28 : 0;
    const bodyH = Math.max(0.5, y + h - pad - cursor - metaH - (hasMeta ? 0.08 : 0));
    s.addText(step.body ?? "", {
      x: x + pad,
      y: cursor,
      w: cardW - pad * 2,
      h: bodyH,
      fontFace: FONT,
      fontSize: 12,
      color: palette.muted,
      margin: 0,
      wrap: true,
      valign: "top",
    });

    if (hasMeta) {
      s.addText(
        [
          {
            text: `${String(step.metaLabel ?? "").toUpperCase()} `,
            options: { bold: true, color: palette.accent, fontSize: 9, fontFace: FONT },
          },
          {
            text: step.metaValue ?? "",
            options: { color: palette.text, fontSize: 10, fontFace: FONT },
          },
        ],
        {
          x: x + pad,
          y: y + h - pad - metaH,
          w: cardW - pad * 2,
          h: metaH,
          margin: 0,
          valign: "middle",
          wrap: true,
        },
      );
    }

    if (index < count - 1 && arrowW) {
      s.addText("→", {
        x: x + cardW,
        y: y + h / 2 - 0.14,
        w: arrowW + gap,
        h: 0.28,
        fontFace: FONT,
        fontSize: 14,
        color: palette.accent,
        align: "center",
        valign: "middle",
        margin: 0,
      });
    }
  });
}

function addMediaSlide(pptx, slideModel, ctx) {
  const palette = paletteFor(slideModel);
  const s = createChromedSlide(pptx, slideModel, ctx, palette, "media");
  const header = addSectionChrome(s, pptx, palette, slideModel);
  const y = header.contentY;
  const h = header.contentH;
  const hasTakeaway = Boolean(slideModel.takeaway);
  const takeH = hasTakeaway ? 0.85 : 0;
  const upperH = h - (hasTakeaway ? takeH + 0.14 : 0);
  const imageW = 4.4;
  const textX = TM.marginX + imageW + 0.28;
  const textW = TM.width - TM.marginX - textX;

  s.addShape(pptx.ShapeType.roundRect, {
    x: TM.marginX,
    y,
    w: imageW,
    h: upperH,
    rectRadius: TM.radius,
    fill: { color: palette.placeholderFill },
    line: cardLine(palette),
  });

  if (slideModel.imageLabel) {
    s.addText(String(slideModel.imageLabel).toUpperCase(), {
      x: TM.marginX + 0.18,
      y: y + upperH - 0.4,
      w: Math.min(2.4, imageW - 0.36),
      h: 0.24,
      fontFace: FONT,
      fontSize: 9,
      bold: true,
      color: "1A1A1A",
      align: "center",
      valign: "middle",
      fill: { color: palette.accent },
      shape: pptx.ShapeType.roundRect,
    });
  }

  const headingH = textBoxHeight(slideModel.heading, textW, 16, {
    maxLines: 2,
    bold: true,
    min: 0.32,
  });
  s.addText(slideModel.heading ?? "", {
    x: textX,
    y,
    w: textW,
    h: headingH,
    fontFace: FONT,
    fontSize: 16,
    bold: true,
    color: palette.text,
    margin: 0,
    wrap: true,
  });

  const hasCallout = Boolean(slideModel.callout);
  const calloutH = hasCallout ? 0.62 : 0;
  const bodyH = Math.max(0.6, upperH - headingH - 0.12 - (hasCallout ? calloutH + 0.1 : 0));
  s.addText(slideModel.body ?? "", {
    x: textX,
    y: y + headingH + 0.1,
    w: textW,
    h: bodyH,
    fontFace: FONT,
    fontSize: 12,
    color: palette.muted,
    margin: 0,
    wrap: true,
    valign: "top",
  });

  if (hasCallout) {
    const callY = y + headingH + 0.1 + bodyH + 0.08;
    s.addShape(pptx.ShapeType.roundRect, {
      x: textX,
      y: callY,
      w: textW,
      h: calloutH,
      rectRadius: 0.08,
      fill: { color: palette.calloutFill },
      line: { color: palette.calloutFill, width: 0 },
    });
    s.addText(slideModel.callout, {
      x: textX + 0.12,
      y: callY + 0.06,
      w: textW - 0.24,
      h: calloutH - 0.12,
      fontFace: FONT,
      fontSize: 11,
      color: palette.muted,
      margin: 0,
      wrap: true,
      valign: "middle",
    });
  }

  if (hasTakeaway) {
    const takeY = y + upperH + 0.14;
    s.addShape(pptx.ShapeType.roundRect, {
      x: TM.marginX,
      y: takeY,
      w: contentWidth(),
      h: takeH,
      rectRadius: 0.08,
      fill: { color: palette.takeawayFill },
      line: { color: palette.takeawayFill, width: 0 },
    });
    s.addShape(pptx.ShapeType.rect, {
      x: TM.marginX,
      y: takeY,
      w: 0.045,
      h: takeH,
      fill: { color: palette.accent },
      line: { color: palette.accent, width: 0 },
    });
    s.addText("KEY TAKEAWAY", {
      x: TM.marginX + 0.18,
      y: takeY + 0.08,
      w: contentWidth() - 0.32,
      h: 0.2,
      fontFace: FONT,
      fontSize: 10,
      bold: true,
      color: palette.accent,
      margin: 0,
    });
    s.addText(slideModel.takeaway, {
      x: TM.marginX + 0.18,
      y: takeY + 0.28,
      w: contentWidth() - 0.32,
      h: takeH - 0.38,
      fontFace: FONT,
      fontSize: 11,
      color: palette.text,
      margin: 0,
      wrap: true,
      valign: "top",
    });
  }
}

const TEACHER_MANUAL_KINDS = new Set([
  "cover",
  "divider",
  "overview",
  "compare",
  "steps",
  "media",
]);

export function isTeacherManualSlide(slide) {
  return TEACHER_MANUAL_KINDS.has(slide?.kind);
}

export function addTeacherManualSlide(pptx, slide, index) {
  const ctx = { index };
  switch (slide.kind) {
    case "cover":
      addCoverSlide(pptx, slide, ctx);
      break;
    case "divider":
      addDividerSlide(pptx, slide, ctx);
      break;
    case "overview":
      addOverviewSlide(pptx, slide, ctx);
      break;
    case "compare":
      addCompareSlide(pptx, slide, ctx);
      break;
    case "steps":
      addStepsSlide(pptx, slide, ctx);
      break;
    case "media":
      addMediaSlide(pptx, slide, ctx);
      break;
    default:
      break;
  }
}
